// Verbatim v1 tunables. Every number here is copied from the v1 source cited
// next to it — do NOT "improve" values in this file; game feel depends on them.

// js/constants.js:312-321 (GAME_CONSTANTS)
export const LANES = [-4, -1.5, 1.5, 4] as const;
export const LANE_TYPES = ['pavement', 'street', 'street', 'pavement'] as const;
export const GRAVITY = -0.022;
export const JUMP_VELOCITY = 0.32;
export const SPAWN_DISTANCE = -35;
export const COLLISION_DISTANCE = 0.8;
export const COIN_COLLECTION_DISTANCE = 0.8;
export const MAX_GAME_SPEED = 0.25;

export type LaneIndex = 0 | 1 | 2 | 3;
export type LaneType = (typeof LANE_TYPES)[number];

// js/player-manager.js:902 (double jump), :812 (flip duration), :651 (lane lerp)
export const DOUBLE_JUMP_FACTOR = 0.85;
export const FLIP_DURATION = 0.55;
export const LANE_LERP_RATE = 12; // lerpSpeed = min(12 * dt, 0.3)
export const LANE_LERP_CAP = 0.3;

// js/player-manager.js:624-633 — player origin sits 0.19 above the shoe sole
export const SHOE_BOTTOM_OFFSET = 0.19;
export const GROUND_Y_PAVEMENT = 0.08 + SHOE_BOTTOM_OFFSET; // 0.27
export const GROUND_Y_STREET = 0.05 + SHOE_BOTTOM_OFFSET; // 0.24

// js/player-manager.js:687 — air resistance factor on gravity integration
export const AIR_RESISTANCE_COEFF = 0.02;

// js/player-manager.js:101 — player z position (world scrolls, player stays)
export const PLAYER_Z = 2;

// js/game3d.js:497 — frame delta clamp; fixed step replicates dt*60 == 1
export const FIXED_STEP = 1 / 60;
export const MAX_FRAME_DELTA = 0.033;

// js/game3d.js:556,563,568,574-576,581 — spawn intervals & difficulty ramp
export const OBSTACLE_BASE_INTERVAL = (gameSpeed: number): number => 1.5 - gameSpeed * 5;
export const OBSTACLE_MIN_INTERVAL = 0.6;
export const COIN_SPAWN_INTERVAL = 1.5;
export const DIFFICULTY_INTERVAL = 10;
export const DIFFICULTY_SPEED_STEP = 0.01;
export const POWER_UP_SPAWN_INTERVAL = 20;

// js/game3d.js:613 — distance accrual per second of unscaled time
export const DISTANCE_FACTOR = 100;

// js/constants.js:645-656 (COMBO_CONFIG)
export const COMBO_TIERS = [
  { threshold: 0, multiplier: 1.0 },
  { threshold: 5, multiplier: 1.5 },
  { threshold: 10, multiplier: 2.0 },
  { threshold: 20, multiplier: 3.0 },
  { threshold: 50, multiplier: 5.0 },
] as const;
export const COMBO_DECAY_TIME = 4.0;

// js/constants.js:659-664 (NEAR_MISS_CONFIG)
export const NEAR_MISS = {
  ZONE_MULTIPLIER: 1.8,
  BONUS_POINTS: 25,
  COOLDOWN: 0.5,
  HEIGHT_THRESHOLD: 1.2,
} as const;

// js/constants.js:324-328 (SHIELD_CONFIG — Second Wind)
export const SHIELD = {
  GRACE_DURATION: 2.0,
  SPEED_REDUCTION: 0.04,
  BLINK_HZ: 10,
} as const;

// js/constants.js:667-672 (COIN_MAGNET_CONFIG)
export const COIN_MAGNET = {
  RADIUS: 2.5,
  ATTRACTION_SPEED: 3.0,
  ACCELERATION: 1.5,
  SPIN_MULTIPLIER: 3.0,
} as const;

// js/game3d.js:730-731 — base car collision half-extents (average sedan)
export const CAR_BASE_HALF_WIDTH = 0.75;
export const CAR_BASE_HALF_LENGTH = 1.5;
// js/game3d.js:744 — default car roof height, :758 landing zone tolerance
export const CAR_TOP_HEIGHT = 1.3;
export const CAR_LANDING_TOLERANCE = 0.25;
// js/game3d.js:765 — roof ride bonus, :874 construction clear bonus
export const ROOF_RIDE_POINTS = 25;
export const CONSTRUCTION_CLEAR_POINTS = 30;
// js/game3d.js:916+ (collectCoin) — coin score value
export const COIN_POINTS = 10;

// js/obstacle-manager.js:153-154 — spawn type distribution
export const CAR_SPAWN_CHANCE = 0.65;
export const CONSTRUCTION_SPAWN_CHANCE = 0.15; // randomValue < 0.8
// js/obstacle-manager.js:181 — car speed = baseSpeed * (1.8 + rand*0.4) * districtMult
export const CAR_SPEED_BASE_FACTOR = 1.8;
export const CAR_SPEED_RANDOM_RANGE = 0.4;
// js/obstacle-manager.js:179,184 — car spawn z per direction
export const CAR_SPAWN_Z_ONCOMING = -50;
export const CAR_SPAWN_Z_AWAY = 10;
// js/obstacle-manager.js:351 — coin spacing within patterns
export const COIN_SPACING = 1.4;

// js/scene-manager.js:370-372 — camera
export const CAMERA_FOV = 75;
export const CAMERA_NEAR = 0.5;
export const CAMERA_FAR = 200;
export const CAMERA_POSITION = { x: 0, y: 3, z: 5 } as const;
export const CAMERA_LOOK_AT = { x: 0, y: 1, z: 0 } as const;

// District table lives in content/districts.ts (verbatim v1 values there).

// --- v2 additions (Phase 5): slide move + slide obstacles ---
export const SLIDE = {
  DURATION: 0.7, // Seconds a slide lasts
  COLLIDER_HEIGHT_STANDING: 1.8,
  COLLIDER_HEIGHT_SLIDING: 0.7,
  SWEEP_RANGE: 1.0, // Sliding through an enemy's legs = sweep
} as const;

// Box-truck trailer: slide under (trailer bottom above a slider, below a runner)
export const TRUCK = {
  TRAILER_CLEARANCE: 0.95, // Bottom of trailer above road
  TRAILER_TOP: 2.4, // Too high to jump — slide is the answer
  SPAWN_CHANCE: 0.22, // Share of car spawns that are box trucks
} as const;

// Low pavement barrier: slide-only obstacle
export const BARRIER = {
  BAR_LOW: 0.75, // Bar band a standing/jumping player collides with
  BAR_HIGH: 1.35,
  MIN_GAP_TO_JUMP_OBSTACLE: 14, // Fairness: reaction room between slide+jump obstacles
} as const;
